import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig";
import { formatReportDate } from "../utils/reportDate";
import "./Dashboard.css";
import DashboardReminders from "../components/DashboardReminders";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [company, setCompany] = useState(null);
  const [deals, setDeals] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [shippers, setShippers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (err) {
      return {};
    }
  }, []);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      setError("");

      const results = await Promise.allSettled([
        axios.get("/company"),
        axios.get("/newdeal"),
        axios.get("/invoices"),
        axios.get("/buyers"),
        axios.get("/shippers"),
        axios.get("/banks"),
        axios.get("/items"),
        axios.get("/newdeal/dashboard-summary")
      ]);

      const [companyRes, dealsRes, invoicesRes, buyersRes, shippersRes, banksRes, itemsRes, summaryRes] = results;
      if (summaryRes.status === "fulfilled") setSummary(summaryRes.value.data);
      else setError("Unable to load dashboard counts. Please try again.");

      if (companyRes.status === "fulfilled") {
        const data = companyRes.value.data;
        setCompany(data && Object.keys(data).length > 0 ? data : null);
      }
      if (dealsRes.status === "fulfilled") setDeals(Array.isArray(dealsRes.value.data) ? dealsRes.value.data : []);
      if (invoicesRes.status === "fulfilled") setInvoices(Array.isArray(invoicesRes.value.data) ? invoicesRes.value.data : []);
      if (buyersRes.status === "fulfilled") setBuyers(Array.isArray(buyersRes.value.data) ? buyersRes.value.data : []);
      if (shippersRes.status === "fulfilled") setShippers(Array.isArray(shippersRes.value.data) ? shippersRes.value.data : []);
      if (banksRes.status === "fulfilled") setBanks(Array.isArray(banksRes.value.data) ? banksRes.value.data : []);
      if (itemsRes.status === "fulfilled") setItems(Array.isArray(itemsRes.value.data) ? itemsRes.value.data : []);

      if (results.every((result) => result.status === "rejected")) {
        setError("Unable to load dashboard data.");
      }

      setLoading(false);
    };

    loadDashboard();
  }, []);

  const statusNames = { 1: "New", 2: "Cancel", 3: "Closed", 4: "Ongoing", 5: "Claim" };
  const closedDeals = deals.filter((deal) => Number(deal.DealStatus) === 3).length;
  const pendingWorkflows = deals.filter((deal) => Number(deal.PendingStatus) > 0 && Number(deal.PendingStatus) < 5).length;
  const totalInvoiceAmount = invoices.reduce((sum, invoice) => sum + Number(invoice.AmountInRs || 0), 0);

  const recentDeals = [...deals]
    .sort((a, b) => new Date(b.DealDate || 0) - new Date(a.DealDate || 0))
    .slice(0, 5);

  const role = summary?.role || "other";
  const quickActions = [
    { title: "Create New Deal", text: "Start a fresh trade flow and move it through contracts and shipment.", path: "/transaction/new-deal/add" },
    { title: "Raise Invoice", text: "Capture invoicing activity and keep shipment billing current.", path: "/transaction/new-invoice/add" },
    { title: "Review Deals", text: "Open deal list and continue pending operational stages.", path: "/transaction/new-deal/list" },
    { title: "Company Setup", text: "Maintain company, tax, bank, and contact information.", path: "/tools/company-setting" }
  ].filter(action => role === "admin" ||
    (role === "shipping" ? action.title === "Review Deals" :
      role === "accounts" ? ["Raise Invoice", "Review Deals"].includes(action.title) : action.title === "Review Deals"));
  const statCards = [
    { label: "Deals This Month", value: summary?.monthlyDeals, note: "Based on deal date", accent: "sun" },
    { label: "100% Advance Payment Overdue", value: summary?.fullAdvanceOverdue,
      note: "Due immediately after sales contract is saved", accent: "rose" },
    ...(role === "admin" ? [
      { label: "Total Unfixed Contracts", value: summary?.unfixedContracts, note: "Active contracts awaiting full fixation", accent: "teal" }
    ] : []),
    ...(["admin", "accounts"].includes(role) ? [
      { label: "Advance Payment Overdue", value: summary?.advanceOverdue, note: "100% advance due immediately; other advances after 10 days", accent: "rose" },
      { label: "Payment Overdue", value: summary?.paymentOverdue, note: "ETA plus the payment days set in the deal", accent: "rose" }
    ] : []),
    ...(["admin", "shipping"].includes(role) ? [
      { label: "Shipment Schedule Overdue", value: summary?.shipmentOverdue, note: "More than 25 days after first advance payment", accent: "blue" }
    ] : []),
    ...(role === "accounts" ? [
      { label: "Invoices", value: invoices.length, note: "Current billing records", accent: "blue" },
      { label: "Invoice Value", value: new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(totalInvoiceAmount), note: "Approximate INR value", accent: "teal" }
    ] : []),
    ...(role === "other" ? [
      { label: "Pending Workflows", value: pendingWorkflows, note: "Deals waiting for the next step", accent: "teal" }
    ] : [])
  ];

  const setupChecklist = [
    { label: "Company profile", ready: Boolean(company?.CompanyName), path: "/tools/company-setting" },
    { label: "Banks", ready: banks.length > 0, path: "/master/bankmaster" },
    { label: "Buyers", ready: buyers.length > 0, path: "/master/buyermaster" },
    { label: "Shippers", ready: shippers.length > 0, path: "/master/shippermaster" },
    { label: "Items", ready: items.length > 0, path: "/master/itemmaster" }
  ];

  const today = formatReportDate(new Date());

  return (
    <div className="dashboard-page">
      <DashboardReminders />
      <section className="dashboard-hero">
        <div className="dashboard-hero-copy">
          <span className="dashboard-kicker">{role === "admin" ? "Admin dashboard" : role === "shipping" ? "Shipping dashboard" : role === "accounts" ? "Accounts dashboard" : "Trading operations center"}</span>
          <h1>Welcome back{user.fullName ? `, ${user.fullName}` : ""}.</h1>
          <p>
            A single workspace for daily deal movement, invoicing, company setup, and shipment follow-through.
          </p>

          <div className="dashboard-hero-actions">
            <button type="button" className="dashboard-primary-btn" onClick={() => navigate(role === "admin" ? "/transaction/new-deal/add" : "/transaction/new-deal/list")}>
              {role === "admin" ? "Start New Deal" : "Review Deals"}
            </button>
            {["admin", "accounts"].includes(role) && (
            <button type="button" className="dashboard-secondary-btn" onClick={() => navigate("/transaction/new-invoice/list")}>
              Open Invoices
            </button>
            )}
          </div>
        </div>

        <div className="dashboard-hero-panel">
          <div className="dashboard-hero-chip">Today</div>
          <div className="dashboard-hero-date">{today}</div>
          <div className="dashboard-hero-company">{company?.CompanyName || "Company profile not configured"}</div>
          <div className="dashboard-hero-note">
            {company?.City || company?.Country
              ? `${company?.City || ""}${company?.City && company?.Country ? ", " : ""}${company?.Country || ""}`
              : "Complete the core masters to make transaction entry smoother."}
          </div>
        </div>
      </section>

      <section className="dashboard-stats-grid">
        {statCards.map(card => (
          <div key={card.label} className={`dashboard-stat-card accent-${card.accent}`}>
            <div className="dashboard-stat-label">{card.label}</div>
            <div className="dashboard-stat-value">{card.value ?? (loading ? "…" : "—")}</div>
            <div className="dashboard-stat-meta">{card.note}</div>
          </div>
        ))}
      </section>

      <section className="dashboard-content-grid">
        {role === "admin" && <div className="dashboard-panel">
          <div className="dashboard-panel-head">
            <div>
              <h2>Operational Snapshot</h2>
              <p>Live counts from the masters and transaction modules.</p>
            </div>
          </div>

          <div className="dashboard-mini-grid">
            <div className="dashboard-mini-card"><span>Buyers</span><strong>{buyers.length}</strong></div>
            <div className="dashboard-mini-card"><span>Shippers</span><strong>{shippers.length}</strong></div>
            <div className="dashboard-mini-card"><span>Banks</span><strong>{banks.length}</strong></div>
            <div className="dashboard-mini-card"><span>Items</span><strong>{items.length}</strong></div>
            <div className="dashboard-mini-card"><span>Closed Deals</span><strong>{closedDeals}</strong></div>
            <div className="dashboard-mini-card"><span>Company Setup</span><strong>{company?.CompanyName ? "Ready" : "Pending"}</strong></div>
          </div>
        </div>}

        <div className="dashboard-panel">
          <div className="dashboard-panel-head">
            <div>
              <h2>Quick Actions</h2>
              <p>Common routes for daily work.</p>
            </div>
          </div>

          <div className="dashboard-action-list">
            {quickActions.map((action) => (
              <Link key={action.path} to={action.path} className="dashboard-action-card">
                <strong>{action.title}</strong>
                <span>{action.text}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-panel-head">
            <div>
              <h2>Recent Deals</h2>
              <p>The latest deals that may need attention.</p>
            </div>
            <Link to="/transaction/new-deal/list" className="dashboard-link-btn">View All</Link>
          </div>

          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Deal ID</th>
                  <th>Date</th>
                  <th>Shipper</th>
                  <th>Buyer</th>
                  <th>Status</th>
                  <th>Stage</th>
                </tr>
              </thead>
              <tbody>
                {recentDeals.length > 0 ? (
                  recentDeals.map((deal) => (
                    <tr key={deal.DealID}>
                      <td>{deal.DealID}</td>
                      <td>{formatReportDate(deal.DealDate)}</td>
                      <td>{deal.ShipperName || "-"}</td>
                      <td>{deal.BuyerName || "-"}</td>
                      <td>{statusNames[Number(deal.DealStatus)] || "-"}</td>
                      <td>{deal.PendingStatusName || deal.PendingStatus || "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="dashboard-empty-cell">
                      {loading ? "Loading dashboard..." : "No deals available yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {role === "admin" && <div className="dashboard-panel">
          <div className="dashboard-panel-head">
            <div>
              <h2>Setup Checklist</h2>
              <p>Complete these masters for a cleaner workflow.</p>
            </div>
          </div>

          <div className="dashboard-checklist">
            {setupChecklist.map((item) => (
              <button
                key={item.label}
                type="button"
                className={`dashboard-check-item ${item.ready ? "ready" : "pending"}`}
                onClick={() => navigate(item.path)}
              >
                <span>{item.label}</span>
                <strong>{item.ready ? "Complete" : "Needs setup"}</strong>
              </button>
            ))}
          </div>
        </div>}
      </section>

      {error ? <div className="dashboard-error">{error}</div> : null}
    </div>
  );
};

export default Dashboard;
